import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Ticket, ArrowRight, CheckCircle2, AlertTriangle, Loader2, LogIn } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /claim — attach a ticket to the current account using a short code.
 *
 * Flow:
 *   1. If not logged in → show a login/signup prompt with return-to link.
 *   2. User types the code (case-insensitive, dashes/spaces tolerated).
 *   3. On success → redirect to /ticket/:id.
 *   4. Clear error states for invalid/revoked/already-claimed cases.
 *
 * Safety: no ticket details are fetched or shown until claim succeeds.
 */
export default function ClaimPass() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggedIn, setLoggedIn] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // { ticket_id, already_claimed }

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('session_token') : null;
    setLoggedIn(Boolean(token));
  }, []);

  // Auto-redirect after successful claim (short delay so the user sees the confirmation)
  useEffect(() => {
    if (success?.ticket_id) {
      const t = setTimeout(() => navigate(`/ticket/${success.ticket_id}`), 900);
      return () => clearTimeout(t);
    }
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(
        `${API}/tickets/claim`,
        { claim_code: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(res.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        // Token expired / missing — redirect to login, preserve intent
        navigate(`/login?return=${encodeURIComponent(location.pathname)}`);
        return;
      }
      setError(detail || 'Could not claim this pass. Please check the code and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#EAE7E1]" data-testid="claim-pass-page">
      {/* Header */}
      <header className="border-b border-[#1A1A1A]">
        <div className="max-w-[520px] mx-auto px-5 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-sm tracking-[0.4em] font-bold text-[#D4AF37] uppercase" data-testid="claim-home-link">VelvetGate</Link>
          <Link to="/" className="text-xs text-[#777] hover:text-[#EAE7E1] transition-colors">← Home</Link>
        </div>
      </header>

      <main className="max-w-[520px] mx-auto px-5 sm:px-6 py-10 sm:py-16">
        <div className="text-[10px] tracking-[0.35em] text-[#D4AF37] uppercase font-semibold mb-3" data-testid="claim-eyebrow">
          Claim Your Pass
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#F3EEE7] mb-3" data-testid="claim-title">
          Enter Your Claim Code
        </h1>
        <p className="text-sm sm:text-base text-[#B9B1A6] leading-relaxed mb-8" data-testid="claim-description">
          Paste the 8-character code from your invitation or receipt. We&rsquo;ll attach the pass to your VelvetGate account.
        </p>

        {/* State 1 — not logged in */}
        {!loggedIn && (
          <div
            className="border border-[#D4AF37]/25 rounded-sm bg-gradient-to-br from-[#0F0D08] to-[#050505] p-5"
            data-testid="claim-login-prompt"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                <LogIn className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#F3EEE7] mb-1">Sign in to claim</div>
                <p className="text-sm text-[#B9B1A6] leading-relaxed">
                  You need an account to claim a pass. It keeps your pass secure and lets you re-download it anytime.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={`/login?return=${encodeURIComponent('/claim')}`}
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] px-4 py-2.5 rounded-sm font-bold text-sm transition-opacity"
                    data-testid="claim-login-button"
                  >
                    Sign In <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/signup?return=${encodeURIComponent('/claim')}`}
                    className="inline-flex items-center justify-center gap-2 border border-[#1A1A1A] text-[#EAE7E1] hover:border-[#D4AF37]/40 hover:bg-[#0D0D0D] px-4 py-2.5 rounded-sm font-semibold text-sm transition-colors"
                    data-testid="claim-signup-button"
                  >
                    Create Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* State 2 — success */}
        {loggedIn && success && (
          <div
            className="border border-emerald-500/25 rounded-sm bg-gradient-to-br from-[#082018] to-[#050505] p-6"
            data-testid="claim-success-state"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-bold text-[#F3EEE7] mb-1" data-testid="claim-success-heading">
                  {success.already_claimed ? 'Already on your account' : 'Pass claimed successfully'}
                </div>
                <p className="text-sm text-[#B9B1A6] leading-relaxed">
                  Opening your pass&hellip;
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-[#777]">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Redirecting to /ticket/{success.ticket_id}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* State 3 — form */}
        {loggedIn && !success && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="claim-form">
            <div>
              <label htmlFor="claim-code" className="text-xs tracking-[0.15em] uppercase text-[#777] font-semibold">
                Claim Code
              </label>
              <div className="relative mt-1.5">
                <Ticket className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  id="claim-code"
                  type="text"
                  required
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={code}
                  onChange={(e) => { setCode(e.target.value); if (error) setError(null); }}
                  placeholder="e.g. 2AKJZ3TW"
                  className="w-full pl-10 pr-3 py-3 rounded-sm bg-[#0A0A0A] border border-[#1A1A1A] text-[#EAE7E1] placeholder-[#555] font-mono uppercase tracking-widest focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                  data-testid="claim-code-input"
                />
              </div>
              <p className="text-[11px] text-[#555] mt-1.5">
                Case-insensitive. Dashes and spaces are ignored.
              </p>
            </div>

            {error && (
              <div
                className="flex items-start gap-3 border border-[#C44545]/25 bg-[#C44545]/5 rounded-sm p-4"
                data-testid="claim-error-state"
              >
                <AlertTriangle className="w-5 h-5 text-[#C44545] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#B9B1A6] leading-relaxed" data-testid="claim-error-message">
                  {error}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !code.trim()}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 disabled:opacity-50 text-[#050505] px-6 py-3.5 rounded-sm font-bold text-sm transition-opacity"
              data-testid="claim-submit-button"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Claiming&hellip;</>
              ) : (
                <>Claim Pass <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>
        )}

        <div className="mt-10 pt-6 border-t border-[#1A1A1A] flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#777]">
          <Link to="/find-pass" className="hover:text-[#D4AF37] transition-colors" data-testid="claim-find-pass-link">Lost your link?</Link>
          <Link to="/faq"       className="hover:text-[#D4AF37] transition-colors">FAQ</Link>
          <Link to="/contact"   className="hover:text-[#D4AF37] transition-colors">Contact</Link>
          <span className="ml-auto text-[#444]">© VelvetGate</span>
        </div>
      </main>
    </div>
  );
}
